"use client"

import { useActionState, useRef, useState } from "react"
import { LOGIN_VIEW } from "@modules/account/templates/login-template"
import ErrorMessage from "@modules/checkout/components/error-message"
import { SubmitButton } from "@modules/checkout/components/submit-button"
import { signup } from "@lib/data/customer"
import GoogleAuthButton from "@modules/account/components/google-auth-button"
import PaypalAuthButton from "@modules/account/components/paypal-auth-button"
import ReCAPTCHA from "react-google-recaptcha"

type Props = {
  setCurrentView: (view: LOGIN_VIEW) => void
}

const Register = ({ setCurrentView }: Props) => {
  const [formState, formAction] = useActionState(signup, null)
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (password !== confirmPassword) {
      e.preventDefault()
      setConfirmError("Passwords do not match")
      return
    }
    setConfirmError(null)

    if (!recaptchaToken) {
      e.preventDefault()
      setRecaptchaError("Please verify you are not a robot")
      return
    }
    setRecaptchaError(null)
  }

  return (
    <div data-testid="register-page">
      <h2 className="text-[22px] font-extrabold text-center text-gray-900 mt-7 mb-1.5 uppercase tracking-[1.1px]">
        Sign Up With Email
      </h2>
      <p className="text-center text-[15px] text-gray-500 mb-5">
        To create your account
      </p>

      <form className="w-full" action={formAction} onSubmit={handleSubmit}>
        <div className="space-y-[15px]">
          <div className="grid grid-cols-2 gap-4">
            <input
              name="first_name"
              type="text"
              placeholder="First Name"
              required
              autoComplete="given-name"
              className="w-full h-12 px-4 border border-brand-navy rounded-lg text-[15px] text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy outline-none"
              data-testid="first-name-input"
            />

            <input
              name="last_name"
              type="text"
              placeholder="Last Name"
              required
              autoComplete="family-name"
              className="w-full h-12 px-4 border border-brand-navy rounded-lg text-[15px] text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy outline-none"
              data-testid="last-name-input"
            />
          </div>

          <input
            name="email"
            type="email"
            placeholder="Email"
            required
            autoComplete="email"
            className="w-full h-12 px-4 border border-brand-navy rounded-lg text-[15px] text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy outline-none"
            data-testid="email-input"
          />

          <div>
            <div className="grid grid-cols-2 gap-4">
              <input
                name="password"
                type="password"
                placeholder="Password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 px-4 border border-brand-navy rounded-lg text-[15px] text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy outline-none"
                data-testid="password-input"
              />

              <input
                name="confirm_password"
                type="password"
                placeholder="Confirm Password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full h-12 px-4 border border-brand-navy rounded-lg text-[15px] text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-navy outline-none"
                data-testid="confirm-password-input"
              />
            </div>
            <p className="text-[11px] text-gray-400 mt-[3px] px-1">
              Must be at least 6 characters
            </p>
          </div>
        </div>

        <ErrorMessage
          error={confirmError ?? formState?.error}
          data-testid="register-error"
          className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
        />

        <label className="flex items-center gap-2 text-sm text-gray-700 mt-[18px] cursor-pointer">
          <input
            name="accepts_marketing"
            type="checkbox"
            defaultChecked
            className="w-4 h-4 rounded border-gray-300"
            data-testid="accepts-marketing-checkbox"
          />
          Keep me up to date on news and exclusive offers
        </label>

        <div className="mt-3">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
            onChange={(token) => {
              setRecaptchaToken(token)
              setRecaptchaError(null)
            }}
            onExpired={() => setRecaptchaToken(null)}
          />
          <ErrorMessage
            error={recaptchaError}
            data-testid="recaptcha-error"
            className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
          />
        </div>

        <SubmitButton
          data-testid="register-button"
          className="w-full h-[52px] mt-[15px] bg-brand-dark-navy !text-[17px] !rounded-full hover:bg-[#0c2354] transition-colors"
        >
          Register
        </SubmitButton>
      </form>

      <div className="mt-[22px] space-y-3">
        <GoogleAuthButton label="Sign up with Google" />
        <PaypalAuthButton />
      </div>
    </div>
  )
}

export default Register
